import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';

const LandingPage = () => {
    const canvasRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!canvasRef.current) return;

        // --- Three.js Setup ---
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        
        // Match parents style or handle sizes
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        canvasRef.current.appendChild(renderer.domElement);

        // --- Create Particles ---
        const particlesCount = 2500;
        const positions = new Float32Array(particlesCount * 3);
        const colors = new Float32Array(particlesCount * 3);

        const colorGold = new THREE.Color('#eab308');
        const colorBlue = new THREE.Color('#0284c7');
        const colorWhite = new THREE.Color('#f8fafc');

        for(let i = 0; i < particlesCount; i++) {
            const i3 = i * 3;
            positions[i3] = (Math.random() - 0.5) * 12;
            positions[i3 + 1] = (Math.random() - 0.5) * 12;
            positions[i3 + 2] = (Math.random() - 0.5) * 8;

            let mixedColor = colorWhite;
            const rand = Math.random();
            if (rand < 0.35) mixedColor = colorGold;
            else if (rand < 0.55) mixedColor = colorBlue;

            colors[i3] = mixedColor.r;
            colors[i3 + 1] = mixedColor.g;
            colors[i3 + 2] = mixedColor.b;
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

        const material = new THREE.PointsMaterial({
            size: 0.04,
            vertexColors: true,
            transparent: true,
            opacity: 0.85,
            sizeAttenuation: true,
            blending: THREE.AdditiveBlending
        });

        const particlesMesh = new THREE.Points(geometry, material);
        scene.add(particlesMesh);

        camera.position.z = 4;

        // --- Mouse Interaction ---
        let mouseX = 0, mouseY = 0, targetX = 0, targetY = 0;
        
        const handleMouseMove = (event) => {
            mouseX = (event.clientX / window.innerWidth - 0.5) * 0.5;
            mouseY = (event.clientY / window.innerHeight - 0.5) * 0.5;
        };
        window.addEventListener('mousemove', handleMouseMove);

        const clock = new THREE.Clock();
        
        // --- Animation Frame loop ---
        let animationFrameId;
        const animate = () => {
            animationFrameId = requestAnimationFrame(animate);

            const elapsedTime = clock.getElapsedTime();

            particlesMesh.rotation.y = elapsedTime * 0.04;
            particlesMesh.rotation.x = elapsedTime * 0.015;

            // Smooth follow
            targetX += (mouseX - targetX) * 0.05;
            targetY += (mouseY - targetY) * 0.05;

            particlesMesh.position.x = targetX;
            particlesMesh.position.y = -targetY;

            renderer.render(scene, camera);
        };
        animate();

        // --- Window Resize ---
        const handleResize = () => {
            camera.aspect = window.innerWidth / window.innerHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(window.innerWidth, window.innerHeight);
        };
        window.addEventListener('resize', handleResize);

        // --- Cleanup on Unmount ---
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('resize', handleResize);
            cancelAnimationFrame(animationFrameId);
            renderer.dispose();
            geometry.dispose();
            material.dispose();
            
            if (canvasRef.current && renderer.domElement) {
                canvasRef.current.removeChild(renderer.domElement);
            }
        };
    }, []);

    const handleEnter = () => {
        navigate('/dashboard');
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            width: '100%', height: '100%',
            overflow: 'hidden', backgroundColor: '#0f172a', zIndex: 1000
        }}>
            {/* 3D Canvas Container */}
            <div ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }} />
            
            {/* HTML Overlay with glassmorphism blending */}
            <div style={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                textAlign: 'center', zIndex: 10, width: '90%', pointerEvents: 'none'
            }}>
                <h1 className="landing-title" style={{
                    fontFamily: "'Orbitron', sans-serif", fontWeight: 900,
                    letterSpacing: '6px', background: 'linear-gradient(135deg, #fef08a 0%, #eab308 50%, #ca8a04 100%)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    textShadow: '0 0 35px rgba(234, 179, 8, 0.4)', marginBottom: '14px',
                    animation: 'float 4s ease-in-out infinite alternate'
                }}>BASEBALL FOOD</h1>
                
                <p className="landing-subtitle" style={{ 
                    opacity: 0.85, color: '#94a3b8', 
                    letterSpacing: '2px', marginBottom: '45px' 
                }}>
                    PREMIUM DIGITAL MANAGEMENT SYSTEM
                </p>

                <div style={{ pointerEvents: 'auto', display: 'inline-block' }}>
                    <button 
                        onClick={handleEnter}
                        className="enter-btn-hover"
                        style={{
                            padding: '16px 52px', background: 'rgba(15, 23, 42, 0.7)',
                            backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                            border: '1px solid rgba(234, 179, 8, 0.35)', color: '#ffffff',
                            borderRadius: '50px', cursor: 'pointer', fontWeight: 'bold',
                            fontSize: '1.05rem', letterSpacing: '1px', transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)'
                        }}
                    >
                        시스템 진입하기
                    </button>
                </div>
            </div>

            {/* Custom Styles for Button Hover and Responsive text */}
            <style>{`
                @keyframes float {
                    0% { transform: translateY(0px); }
                    100% { transform: translateY(-8px); }
                }
                .landing-title {
                    font-size: 3.5rem;
                }
                .landing-subtitle {
                    font-size: 1.1rem;
                }
                .enter-btn-hover:hover {
                    transform: translateY(-5px);
                    background: rgba(234, 179, 8, 0.15) !important;
                    border-color: rgba(234, 179, 8, 0.9) !important;
                    box-shadow: 0 0 30px rgba(234, 179, 8, 0.4) !important;
                    color: #fef08a !important;
                }
                @media (max-width: 768px) {
                    .landing-title {
                        font-size: 1.8rem !important;
                        letter-spacing: 2px !important;
                    }
                    .landing-subtitle {
                        font-size: 0.85rem !important;
                        margin-bottom: 35px !important;
                    }
                    .enter-btn-hover {
                        padding: 13px 45px !important;
                        font-size: 0.92rem !important;
                    }
                }
            `}</style>


        </div>
    );
};

export default LandingPage;
